import { useEffect, useState } from 'react'

// ============================================================================
// THE PHONE'S MOTION SWITCHES — docs/design/handoff-mobile-v10 (boards 03, 25, 27) and
// docs/design/mobile-redesign-handoff.md §Motion spec.
//
// Three things live here so each has exactly one home:
//   · MOBILE_GOO — the runtime flag for the tab bar's gooey highlight. The SVG goo filter
//     (feGaussianBlur + feColorMatrix) is the one effect on the bar that could cost frames on a
//     phone GPU; flipping this to false gives the plain single sliding pill everywhere.
//   · useReducedMotion — the OS switch, read in JS because the shell picks DIFFERENT classes
//     under it (a 160ms fade instead of a slide), not merely shorter ones.
//   · buzz — the one haptic call. `navigator.vibrate` is absent on iOS and may be refused in a
//     WebView without the permission; a missing buzz is never an error.
// ============================================================================

/** The gooey two-pill highlight on the tab bar. False = one plain sliding pill. */
export const MOBILE_GOO = true

export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

/** The highlight the bar draws: two pills under the goo filter, one sliding pill, or one still pill. */
export type PillMode = 'goo' | 'solo' | 'still'

/** Reduced motion wins over the flag: the fallback is used AND it does not animate. */
export function pillMode(goo: boolean, reduced: boolean): PillMode {
  if (reduced) return 'still'
  return goo ? 'goo' : 'solo'
}

function readReduced(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? Boolean(window.matchMedia(REDUCED_MOTION_QUERY).matches)
      : false
  } catch {
    return false
  }
}

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(readReduced)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const query = window.matchMedia(REDUCED_MOTION_QUERY)
    const onChange = () => setReduced(Boolean(query.matches))
    query.addEventListener?.('change', onChange)
    return () => query.removeEventListener?.('change', onChange)
  }, [])
  return reduced
}

/**
 * THE BEATS JS HAS TO WAIT FOR. The curves and the animations themselves are CSS (index.css,
 * `[data-shell='mobile']`, where the same numbers are the `--m-dur-*` tokens); these are only the
 * moments a timer must know about — when a leaving page can be unmounted.
 */
export const MOTION_MS = {
  /** Board 25 push/back: 340ms `cubic-bezier(.32,.72,0,1)`. */
  push: 340,
  /** Tab fade-through: out 90ms, then in after a 90ms delay — the rise is the long half (220ms). */
  tab: 310,
  /** Reduced motion: every transition is a 160ms opacity fade. */
  fade: 160,
  /** Sheet in: 360ms spring. */
  sheetIn: 360,
  /** Sheet out and its 42% scrim: 220ms. */
  sheetOut: 220,
} as const

/** The haptic tick. 8ms per tab crossing and per menu open (board 03); never on scroll. */
export const BUZZ_MS = 8

export function buzz(ms: number = BUZZ_MS): void {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') navigator.vibrate(ms)
  } catch {
    // A refused vibration is not an error the owner can do anything about.
  }
}
